import React, {
    useMemo,
    useCallback,
    useEffect,
    useState,
    useRef,
} from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { REVSTACK_CHART_COLORS } from "@ts/constants";
import { titleCase } from "@ts/utils";
import {
    getRevstackColors,
    mergePercentAndNonPercentData,
} from "@ts/revstack/helpers";
import { chartLabelToolTooltipFormatter } from "@ts/revstack/ui-utils";
import { Skeleton } from "antd";
import NoDataText from "./NoDataText";
import { useResponsiveChartHeight } from "@hooks/AllCustomHooks";
import { getRevstackPopoverData } from "@ts/retrieve-data";

interface IRevstackCategoryPerformanceBarWidgetProps {
    widgetData: any;
    widget: any;
    yAxisTickAmount?: number;
    chartComponentRef: React.RefObject<HighchartsReact.RefObject>;
    graphColors?: string[];
    isLoading?: boolean;
    config?: any;
}

if ((Highcharts.Series.prototype as any).drawLegendSymbol) {
    // Disable legend symbol drawing for this chart
    (Highcharts.Series.prototype as any).drawLegendSymbol = function () {
        // Intentionally empty to disable legend symbols
    };
}

const RevstackCategoryPerformanceBarWidget: React.FC<
    IRevstackCategoryPerformanceBarWidgetProps
> = ({
    widgetData,
    widget,
    graphColors,
    chartComponentRef,
    yAxisTickAmount = 5,
    isLoading = false,
}) => {
    const config = widget?.config;
    const graphAttributes = config?.graphAttributes || {};
    const tooltipConfig = config?.tooltipConfig;

    const [tooltipData, setTooltipData] = useState<any[]>([]);
    const tooltipFetchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const lastFetchedCategoryRef = useRef<string | null>(null);
    const isMultipleDimensionsRequired =
        widget.queryDimensions?.length > 1 || false;
    const chartCategoriesDimension =
        config.graphAttributes.chartCategoriesDimension || "Unknown";
    const xAxisLabelMaxLength =
        config.graphAttributes.xAxisLabelMaxLength || null;
    const maxYaxisValue = config?.graphAttributes?.maxYaxisValue || null;
    const { series, categories, allCategories, showNoData } = useMemo(() => {
        const seriesData = (widgetData?.chartDataSeries?.series || []).map(
            (s: any) => ({
                ...s,
                type: "column",
            })
        );
        const rawCategories =
            widgetData?.chartDataSeries?.categories ??
            (isMultipleDimensionsRequired ? {} : []);
        const categoriesData: string[] =
            isMultipleDimensionsRequired && chartCategoriesDimension
                ? rawCategories[chartCategoriesDimension] ?? []
                : Array.isArray(rawCategories)
                ? rawCategories
                : [];
        const hasNoData = !seriesData?.length;

        return {
            series: seriesData,
            categories: categoriesData,
            allCategories: rawCategories,
            showNoData: hasNoData,
        };
    }, [
        widgetData?.chartDataSeries?.series,
        widgetData?.chartDataSeries?.categories,
        isMultipleDimensionsRequired,
        chartCategoriesDimension,
    ]);

    const chartHeight = useResponsiveChartHeight({ config });

    const colors = useMemo(
        () => graphColors || getRevstackColors(REVSTACK_CHART_COLORS),
        [graphColors]
    );

    const yAxisFormatter = useCallback(
        function (this: Highcharts.AxisLabelsFormatterContextObject) {
            return graphAttributes.yAxisUnit
                ? `${this.value}${graphAttributes.yAxisUnit}`
                : String(this.value);
        },
        [graphAttributes.yAxisUnit]
    );

    const xAxisFormatter = useCallback(
        function (this: Highcharts.AxisLabelsFormatterContextObject) {
            const labelValue = String(this.value);
            const unit = graphAttributes.xAxisUnit || "";
            const fullLabel = `${labelValue}${unit}`;
            const maxLength = xAxisLabelMaxLength;

            if (!maxLength || fullLabel.length <= maxLength) {
                return fullLabel;
            }

            const truncatedLabel = fullLabel.substring(0, maxLength) + "...";

            // Return custom styled tooltip using the formatter
            return chartLabelToolTooltipFormatter(fullLabel, truncatedLabel);
        },
        [graphAttributes.xAxisUnit, xAxisLabelMaxLength]
    );

    const fetchTooltipData = useCallback(
        async (pointName: string) => {
            if (!tooltipConfig?.enableDataFetch) return;

            const skipPopOverFilter =
                typeof pointName === "string" &&
                pointName.toLowerCase().includes("selected");
            try {
                const data = await getRevstackPopoverData(
                    pointName,
                    tooltipConfig.widgetConfig,
                    skipPopOverFilter
                );

                const recordKey =
                    tooltipConfig?.widgetConfig[0]?.queryMetaInfo?.recordKey;
                const mergedData = mergePercentAndNonPercentData(
                    data,
                    recordKey
                );

                setTooltipData(mergedData);
            } catch (error) {
                console.error("Error fetching tooltip data:", error);
                setTooltipData([]);
            }
        },
        [tooltipConfig, mergePercentAndNonPercentData]
    );

    const cancelScheduledTooltipFetch = useCallback(() => {
        if (tooltipFetchTimeoutRef.current) {
            clearTimeout(tooltipFetchTimeoutRef.current);
            tooltipFetchTimeoutRef.current = null;
        }
    }, []);

    const scheduleTooltipFetch = useCallback(
        (pointName: string) => {
            if (!tooltipConfig?.enableDataFetch) return;

            // Don't fetch if we're already showing/fetching data for this category
            if (lastFetchedCategoryRef.current === pointName) {
                return;
            }

            cancelScheduledTooltipFetch();
            // Clear existing tooltip data immediately to prevent showing stale data
            setTooltipData([]);
            lastFetchedCategoryRef.current = pointName;
            const delay = tooltipConfig.fetchDelay ?? 100;
            tooltipFetchTimeoutRef.current = setTimeout(() => {
                fetchTooltipData(pointName);
            }, delay);
        },
        [tooltipConfig, fetchTooltipData, cancelScheduledTooltipFetch]
    );

    const pointMouseOverHandler = useCallback(
        function (this: any) {
            const tooltipCategoryDimension =
                tooltipConfig?.tooltipCategoryDimension;
            const chart = this.series.chart;
            const category = this.category;
            const pointIndex = this.x;

            // Get the sku value using point index when multiple dimensions are available
            const pointValue =
                isMultipleDimensionsRequired &&
                allCategories?.[tooltipCategoryDimension]
                    ? allCategories?.[tooltipCategoryDimension][pointIndex]
                    : category;

            if (chart.xAxis && chart.xAxis[0]) {
                chart.xAxis[0].removePlotBand("hover-band");
                chart.xAxis[0].addPlotBand({
                    from: pointIndex - 0.5,
                    to: pointIndex + 0.5,
                    color: "#F0F4F8",
                    id: "hover-band",
                    zIndex: 0,
                });
            }

            // Only fetch once per category - shared tooltip means all bars in category trigger this
            // Use skuValue for fetching tooltip data
            if (
                tooltipConfig?.enableDataFetch &&
                pointValue &&
                lastFetchedCategoryRef.current !== pointValue
            ) {
                scheduleTooltipFetch(pointValue);
            }
        },
        [
            tooltipConfig,
            scheduleTooltipFetch,
            isMultipleDimensionsRequired,
            allCategories,
        ]
    );

    const pointMouseOutHandler = useCallback(
        function (this: any) {
            const chart = this.series.chart;
            if (chart.xAxis && chart.xAxis[0]) {
                chart.xAxis[0].removePlotBand("hover-band");
            }

            // Only cancel and reset if we're truly leaving all points
            // Check if mouse is still over another point in the same category
            if (
                !chart.hoverPoint ||
                chart.hoverPoint.category !== this.category
            ) {
                cancelScheduledTooltipFetch();
                lastFetchedCategoryRef.current = null;
            }
        },
        [cancelScheduledTooltipFetch]
    );

    const chartLoadHandler = useCallback(function (this: any) {
        if (this.xAxis && this.xAxis[0]) {
            this.xAxis[0].removePlotBand("hover-band");
        }
    }, []);

    useEffect(() => {
        if (
            chartComponentRef?.current?.chart &&
            tooltipConfig?.enableDataFetch
        ) {
            const chart = chartComponentRef.current.chart;

            (chart.userOptions as any).tooltipData = tooltipData;
            (chart.userOptions as any).yAxisUnit =
                graphAttributes?.yAxisUnit || "%";
            (chart.userOptions as any).tooltipMetrics =
                tooltipConfig?.tooltipMetrics || [];
            (chart.userOptions as any).showTooltipPoints =
                tooltipConfig?.showTooltipPoints ?? false;

            if (tooltipData.length > 0 && chart.hoverPoint) {
                chart.tooltip.refresh(chart.hoverPoint);
            }
        }
    }, [tooltipData, chartComponentRef, tooltipConfig, graphAttributes]);

    useEffect(() => {
        return () => {
            cancelScheduledTooltipFetch();
        };
    }, [cancelScheduledTooltipFetch]);

    const chartOptions = useMemo((): Highcharts.Options => {
        // If oppositeYAxisTitle is provided, create two y-axes.
        const yAxis = graphAttributes.oppositeYAxisTitle
            ? [
                  {
                      title: {
                          text: graphAttributes.hideYAxisTitle
                              ? ""
                              : graphAttributes.yAxisTitle || "",
                          align: "middle" as const,
                          style: {
                              color: "#919191",
                              fontFamily: "Manrope, sans-serif",
                          },
                      },
                      labels: {
                          overflow: "justify" as const,
                          style: {
                              color: "#919191",
                              fontFamily: "Manrope, sans-serif",
                          },
                          formatter: yAxisFormatter,
                      },
                      tickAmount: yAxisTickAmount,
                      gridLineDashStyle: "Dash" as Highcharts.DashStyleValue,
                      max: maxYaxisValue,
                  },
                  {
                      title: {
                          text: graphAttributes.hideYAxisTitle
                              ? ""
                              : graphAttributes.oppositeYAxisTitle,
                          align: "middle" as const,
                          style: {
                              color: "#919191",
                              fontFamily: "Manrope, sans-serif",
                          },
                      },
                      labels: {
                          overflow: "justify" as const,
                          style: {
                              color: "#919191",
                              fontFamily: "Manrope, sans-serif",
                          },
                          formatter: yAxisFormatter,
                      },
                      tickAmount: yAxisTickAmount,
                      opposite: true,
                      gridLineDashStyle: "Dash" as Highcharts.DashStyleValue,
                      max: maxYaxisValue,
                  },
              ]
            : {
                  title: {
                      text: graphAttributes.hideYAxisTitle
                          ? ""
                          : graphAttributes.yAxisTitle || "",
                      align: "middle" as const,
                      style: {
                          color: "#919191",
                          fontFamily: "Manrope, sans-serif",
                      },
                  },
                  labels: {
                      overflow: "justify" as const,
                      style: {
                          color: "#919191",
                          fontFamily: "Manrope, sans-serif",
                      },
                      formatter: yAxisFormatter,
                  },
                  tickAmount: yAxisTickAmount,
                  gridLineDashStyle: "Dash" as Highcharts.DashStyleValue,
                  max: maxYaxisValue,
              };

        return {
            chart: {
                type: "column",
                height: chartHeight || 270,
                style: {
                    fontFamily: "Manrope, sans-serif",
                } as unknown as Highcharts.CSSObject,
                spacing: [10, 10, 10, 10],
                events: {
                    load: chartLoadHandler,
                },
            },
            colors,
            title: { text: graphAttributes.title || "" },
            xAxis: {
                categories,
                title: { text: graphAttributes.hideXAxisTitle ? "" : "Dates" },
                lineWidth: 1,
                labels: {
                    style: {
                        color: "var(--Body-Font-Secondary-Color, #919191)",
                        fontFamily: "Manrope, sans-serif",
                        cursor: xAxisLabelMaxLength ? "pointer" : "default",
                    },
                    formatter: xAxisFormatter,
                    useHTML: !!xAxisLabelMaxLength,
                },
            },
            yAxis,
            plotOptions: {
                column: {
                    pointPadding: 0.2,
                    borderWidth: 0,
                    groupPadding: 0.15,
                    states: {
                        hover: {
                            enabled: false,
                            brightness: 0,
                        },
                        inactive: {
                            enabled: false,
                        },
                    },
                },
                series: {
                    marker: {
                        enabled: false,
                        symbol: "circle",
                    },
                    states: {
                        hover: {
                            enabled: false,
                        },
                        inactive: {
                            enabled: false,
                        },
                    },
                    point: {
                        events: {
                            mouseOver: pointMouseOverHandler,
                            mouseOut: pointMouseOutHandler,
                        },
                    },
                },
            },
            legend: {
                align: "center",
                verticalAlign: "top",
                itemDistance: 20,
                labelFormatter: function () {
                    return titleCase(this.name);
                },
            },
            tooltip: {
                useHTML: true,
                backgroundColor: "#23223A",
                borderRadius: 16,
                borderWidth: 0,
                shadow: false,
                padding: 0,
                style: {
                    color: "#fff",
                    fontFamily: "Manrope, sans-serif",
                    fontWeight: "600",
                    fontSize: "22px",
                    textAlign: "right",
                },
                className: "highcharts-tooltip-custom",
                formatter:
                    graphAttributes.tooltipFormatter ||
                    function () {
                        // @ts-ignore
                        return (
                            `<div style="padding: 10px; border-radius: 6px; background: #1E1B39; min-width: 65px;">` +
                            this.points
                                .map(
                                    (point: any, idx: number) => `
                          <div style="display: flex; align-items: center;">
                            <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background: ${
                                point.color
                            }; margin-right: 8px;"></span>
                            <span style="font-size: 12px; font-weight: 700; color: #fff; text-align: right; flex: 1;">${point.y?.toFixed(
                                1
                            )} ${graphAttributes?.yAxisUnit}</span>
                          </div>
                        `
                                )
                                .join("") +
                            "</div>"
                        ).toString();
                    },
                shared: true,
                followPointer: true,
            },
            credits: { enabled: false },
            series,
            lang: { noData: "No data to display" },
            noData: {
                style: {
                    fontWeight: "bold",
                    fontSize: "15px",
                    color: "#303030",
                },
            },
            responsive: {
                rules: [
                    {
                        condition: { maxWidth: 500 },
                        chartOptions: {
                            legend: {
                                align: "center",
                                verticalAlign: "top",
                                layout: "horizontal",
                                y: 10,
                            },
                        },
                    },
                ],
            },
        };
    }, [
        categories,
        series,
        graphAttributes,
        yAxisFormatter,
        xAxisFormatter,
        yAxisTickAmount,
        colors,
        pointMouseOverHandler,
        pointMouseOutHandler,
        chartLoadHandler,
        chartHeight,
        xAxisLabelMaxLength,
    ]);

    const loadingSkeletonHeight = useMemo(() => 270 - 30, []);

    if (isLoading) {
        return (
            <Skeleton.Node
                className="w-100 mt-2"
                active
                style={{ height: loadingSkeletonHeight }}
            />
        );
    }

    if (showNoData) {
        return (
            <div
                className="d-flex align-items-center justify-content-center mt-"
                style={{ height: loadingSkeletonHeight }}
            >
                <NoDataText />
            </div>
        );
    }

    return (
        <HighchartsReact
            highcharts={Highcharts}
            options={chartOptions}
            ref={chartComponentRef}
            containerProps={{
                style: {
                    width: "100%",
                    height: chartHeight,
                },
            }}
        />
    );
};

export default RevstackCategoryPerformanceBarWidget;
